/**
 * Seats (WORKBOARD S6 core half; DIRECTION §12.7, rulings 17 and 19).
 *
 * An **emplacement** is a structure with a seat. A unit in reach may take the empty seat (a
 * mount): it moves onto the emplacement, fires the seat's weapons from there — the mounted
 * classes, the gun's own ammunition, not the unit's — and cannot walk or jump until it
 * dismounts. Taking an enemy's seat captures the emplacement (the "take theirs" half of the
 * on-foot loop). The privilege costs a role and a position (ruling 17): you must be the one
 * in the seat, and you must have walked to it; no charge or cooldown yet.
 *
 * Crew as state (ruling 19): a vehicle's crew exists as records on the unit (`PlayerState.crew`),
 * not as stepped bodies; a unit without the field has the implicit crew of one gunner — which is
 * every unit today, so nothing in the sim reads it yet. The field is here so saves, the sector and
 * the layer-0 dismount have one shape to grow into.
 *
 * Pure functions; `Match.mount` / `Match.dismount` are the actions. The turn bot mounts when `mounting.takeSeats`
 * is on (ai/botBehaviorGraphs.ts, plan step `mount`; off by default, so the goldens are unchanged).
 */

import type { GameState, PlayerState, StructureState, WeaponKey, CrewRecord, Vec3, SeatState, PlanetState } from '../types';
import { getSurfaceRadius } from './surface';
import { arcDistance, getStructureWorldPosition } from './gameState';
import { structureBlastReach } from './damage';
import { personScaleStructureHalfWidth } from './surfaceWalk';
import { structureNearest } from './hits';
import { clockPolicyFor } from '../match/clockPolicy';
import { vecLength, vecSub, vecAdd, vecScale, vecNormalize, vecCross, vecDot, uprightFrame } from '../math';
import { SEAT_POINTS } from '../constants/hitboxes';
import type { BodyKey } from '../constants/body';
import { canSpendMovement } from './clockRules';
import { bodyHasSeat, initialSeats, hatchSurvival } from './body';
import { BODIES } from '../constants/body';
import { killUnit } from './damage';
import { INTERACT_RANGE, MOUNT_MOVEMENT_COST, DISMOUNT_MOVEMENT_COST, WEAPONS, GUN_STOCK } from '../constants';
import { personSetOf, PERSON_BODY_HEIGHT, type PersonSet } from './scaleProfile';
import { unitPointAt, type GroundDials } from './fineTerrain';
import { hatchSeatPos } from './drive';

/** Structure types that carry a seat: the body table says (R12 step 2). */
export function hasSeat(structure: Pick<StructureState, 'type'>): boolean {
  return bodyHasSeat(structure.type);
}

/** The seats a new structure of this type starts with (undefined for the seatless kinds). */
export function initialSeat(type: StructureState['type'], armament?: StructureState['armament'], tankStowage?: { he: number; solid: number }): StructureState['seats'] {
  return initialSeats(type, armament, tankStowage);   // (69.21: a technical's variant in its gun seat; 69.29: a tank's stowage, the dial's)
}

/** The unit in a seat, or null (a crew record or nobody). */
export const seatUnit = (seat: Pick<SeatState, 'occupant'> | undefined): number | null => seat && seat.occupant && 'unitId' in seat.occupant ? seat.occupant.unitId : null;
/** A structure's seat by index, if it has one. */
export const seatAt = (s: Pick<StructureState, 'seats'>, index: number): SeatState | undefined => s.seats?.[index];
/** Is anyone — a unit or a crew record — in this seat? */
export const seatTaken = (seat: Pick<SeatState, 'occupant'> | undefined): boolean => !!seat?.occupant;
/** A structure with the seat at index replaced. */
function withSeat(s: StructureState, index: number, seat: SeatState): StructureState {
  return { ...s, seats: s.seats!.map((x, i) => i === index ? seat : x) };
}

/** The structure a unit sits in, if any (null when not mounted or the structure is gone). */
export function mountedStructure(player: PlayerState, state: Pick<GameState, 'structures'>): StructureState | null {
  if (!player.mount) return null;
  return state.structures.find(s => s.id === player.mount!.bodyId) ?? null;
}
/** The seat a unit sits in, with its structure, or null. */
export function mountedSeat(player: PlayerState, state: Pick<GameState, 'structures'>): { structure: StructureState; seat: SeatState; index: number } | null {
  const structure = mountedStructure(player, state);
  const seat = structure ? seatAt(structure, player.mount!.seat) : undefined;
  return structure && seat ? { structure, seat, index: player.mount!.seat } : null;
}

/** The weapons a seated unit fires from its seat (empty when not mounted). */
export function seatWeapons(player: PlayerState, state: Pick<GameState, 'structures'>): readonly WeaponKey[] {
  return mountedSeat(player, state)?.seat.weapons ?? [];
}

/** Does this shot come from the seat — the gun's ammunition, not the unit's? */
export function isSeatShot(player: PlayerState, weaponKey: WeaponKey, state: Pick<GameState, 'structures'>): boolean {
  return seatWeapons(player, state).includes(weaponKey);
}

/**
 * The unit as a fire check should see it: a shot from the seat draws on the gun's ammunition, so the unit's own
 * count for that weapon does not apply. One helper for every fire path (the match and the game host, R2) so a
 * seated shot the harness accepts is never refused in the browser.
 */
export function fireCheckSubject(player: PlayerState, weaponKey: WeaponKey, state: Pick<GameState, 'structures'>): PlayerState {
  return isSeatShot(player, weaponKey, state)
    ? { ...player, loadout: { ...player.loadout, ammo: { ...player.loadout.ammo, [weaponKey]: null } } }
    : player;
}

export type SeatCheck = { ok: true } | { ok: false; reason: string; code: string };

/**
 * 63.1d: a seat's entry or exit on the ground, as a surface direction — the body's measured seat points (SEAT_POINTS,
 * people) in its own frame: x along its left (up × front), z along its front, the front its drive heading. Null for a body
 * without them (the one-seat rule: boarded and left where it stands) or without a person's size to place them by.
 */
export function seatPoint(s: Pick<StructureState, 'type' | 'surfaceDir' | 'drive'>, index: number, which: 'entry' | 'exit', planetRadius: number, person: number | undefined): Vec3 | null {
  const pts = SEAT_POINTS[s.type as BodyKey]?.[index];
  if (!pts || person === undefined) return null;
  const [x, z] = pts[which];
  const up = vecNormalize(s.surfaceDir);
  // a driven body faces its heading; a fixed one (65.22: the emplaced HMG) the frame it is drawn in (math.ts uprightFrame)
  const h = s.drive?.heading ?? uprightFrame(up).tz;
  const front = vecNormalize(vecSub(h, vecScale(up, vecDot(h, up))));
  const left = vecCross(up, front);
  return vecNormalize(vecAdd(vecScale(up, planetRadius), vecAdd(vecScale(left, x * person), vecScale(front, z * person))));
}

/**
 * Is `index` of a body a seat its occupant sits in where the model seats it — a fixed body's seat with a measured `at`
 * (SEAT_POINTS): 65.22's ground seat (the emplaced HMG's), 63.3's follow-up the gun pit's chair. Its gunner sits crouched
 * there (seatPos), and stands up when it leaves.
 */
export function isSatSeat(type: StructureState['type'], index: number): boolean {
  return BODIES[type]?.mobility === 'fixed' && !!SEAT_POINTS[type as BodyKey]?.[index]?.at;
}

/**
 * Where a gunner sitting in a sat seat is (its seatPos: under its sight eye, which a crouch then lifts to the sitting eye
 * — SEAT_POINTS' `at`) on the structure's ground, in the frame the structure is drawn in: with a `pivot` its gun laid along
 * `heading`, swung about the pivot (65.22's HMG about its tripod's head; 63.1f's follow-up, the gun pit's chair about its
 * turret's axis), else fixed in that frame. Null for any other seat.
 */
export function satSeatPos(s: Pick<StructureState, 'type' | 'surfaceDir'> & Partial<Pick<StructureState, 'baseLift'>>, index: number, heading: Vec3 | undefined, planet: PlanetState, person: number, fine?: GroundDials): Vec3 | null {
  const pts = SEAT_POINTS[s.type as BodyKey]?.[index];
  if (!isSatSeat(s.type, index) || !pts?.at) return null;
  const up = vecNormalize(s.surfaceDir), { tx, tz } = uprightFrame(up);
  // the ground a person there stands on (the fine ground's, S3), not the coarse ground the hit shape stands on (core/hits.ts):
  // on the coarse ground the gunner's eye and muzzle sat under the fine ground's bumps — blind (it saw nobody 20 people
  // off) and firing into the ground (decision 156, the owner: "the bots kind of suck at shooting the 50 cal")
  // (67.52: a seated build stands on its spot's fine ground — its base, StructureState.baseLift — and its gunner with it)
  const base = s.baseLift !== undefined ? vecAdd(planet.position, vecScale(up, getSurfaceRadius(planet, up) + s.baseLift))
    : fine && fine.fineAmplitude > 0 ? unitPointAt(planet, up, fine, 0) : vecAdd(planet.position, vecScale(up, getSurfaceRadius(planet, up)));
  const h = heading && pts.pivot ? vecSub(heading, vecScale(up, vecDot(heading, up))) : tz;
  const front = vecLength(h) > 1e-9 ? vecNormalize(h) : tz, left = vecCross(up, front);
  const [px, pz] = pts.pivot ?? [0, 0], [ax, ay, az] = pts.at;
  const pivot = vecAdd(vecScale(tx, px * person), vecScale(tz, pz * person));
  return vecAdd(base, vecAdd(pivot, vecAdd(vecScale(left, (ax - px) * person), vecAdd(vecScale(front, (az - pz) * person), vecScale(up, ay * person)))));
}

/**
 * May `player` take the seat of `structureId` now? 64.11: the action clock passes a person's reach and no movement
 * cost (it has no movement points); the turn game's defaults are unchanged.
 */
export function canMount(player: PlayerState, structureId: number, state: Pick<GameState, 'structures' | 'planets' | 'players'>, opts: { reach?: number; cost?: number; seat?: number; person?: number } = {}): SeatCheck {
  const reach = opts.reach ?? INTERACT_RANGE, cost = opts.cost ?? MOUNT_MOVEMENT_COST, index = opts.seat ?? 0;
  const s = state.structures.find(x => x.id === structureId);
  if (!s) return { ok: false, reason: 'No such emplacement', code: 'no structure' };
  const seat = seatAt(s, index);
  if (!hasSeat(s) || !seat) return { ok: false, reason: 'Nothing to mount there', code: 'no seat' };
  if (s.health <= 0) return { ok: false, reason: 'That emplacement is destroyed', code: 'destroyed' };
  if (!player.alive || player.airborne) return { ok: false, reason: 'Cannot mount now', code: 'not grounded' };
  if (player.mount) return { ok: false, reason: 'Already mounted — dismount first', code: 'mounted' };
  if (seatTaken(seat) && seatUnit(seat) !== player.id) return { ok: false, reason: 'That seat is taken', code: 'occupied' };
  if (s.planetIndex !== player.planetIndex) return { ok: false, reason: 'Too far to mount', code: 'other planet' };
  if (cost > 0 && player.movementRemaining < cost) return { ok: false, reason: `Not enough movement to mount! (need ${MOUNT_MOVEMENT_COST}, have ${player.movementRemaining.toFixed(1)})`, code: 'movement' };
  const planet = state.planets[player.planetIndex];
  if (!planet) return { ok: false, reason: 'Too far to mount', code: 'other planet' };
  // 63.1d: a seat with an entry (a vehicle's hatch, its ramp) is reached there; else at the structure
  const at = seatPoint(s, index, 'entry', planet.radius, opts.person) ?? s.surfaceDir;
  if (arcDistance(player.surfaceDir, at, planet.radius) > reach) return { ok: false, reason: 'Too far to mount — walk to the seat', code: 'range' };
  return { ok: true };
}

/**
 * Take the seat: the unit stands at the emplacement (the position), the seat records it (the
 * role), the emplacement is the mounter's now (capture), and the movement is spent.
 */
export function mountUnit(state: GameState, playerId: number, structureId: number, cost = MOUNT_MOVEMENT_COST, index = 0): GameState {
  const player = state.players.find(p => p.id === playerId);
  const s = state.structures.find(x => x.id === structureId);
  const seat = s ? seatAt(s, index) : undefined;
  if (!player || !s || !seat) return state;
  // 65.22 / 63.3's follow-up: in a sat seat the gunner sits crouched where the model seats it (an HMG's laid where it faces)
  const planet = state.planets[s.planetIndex];
  const ps = personSetOf(state);
  const sitting = isSatSeat(s.type, index) && planet ? satSeatPos(s, index, player.heading, planet, PERSON_BODY_HEIGHT * ps.k, ps) : null;
  // 156: an exposed hatch (the cupola) — the gunner stands in it, its eye over the hull's top, from the moment it gets in:
  // the hatch's stand was set only as the vehicle drove (stepDrive), so a parked APC's gunner looked and fired from a
  // person's height inside its own hull
  const hatch = !sitting && planet ? hatchSeatPos(planet, s, index, PERSON_BODY_HEIGHT * ps.k, ps) : null;
  return {
    ...state,
    players: state.players.map(p => p.id !== playerId ? p : {
      ...p, mount: { bodyId: structureId, seat: index }, surfaceDir: { ...s.surfaceDir }, movementRemaining: p.movementRemaining - cost,
      ...(sitting ? { seatPos: sitting, stance: 'crouch' as const } : hatch ? { seatPos: hatch } : {}),
    }),
    structures: state.structures.map(x => x.id !== structureId ? x : withSeat({ ...x, ownerId: playerId }, index, { ...seat, occupant: { unitId: playerId } })),
  };
}

export function canDismount(player: PlayerState, state?: Pick<GameState, 'config'>): SeatCheck {
  if (!player.mount) return { ok: false, reason: 'Not mounted', code: 'not mounted' };
  if (!canSpendMovement(state, player, DISMOUNT_MOVEMENT_COST)) return { ok: false, reason: `Not enough movement to dismount! (need ${DISMOUNT_MOVEMENT_COST}, have ${player.movementRemaining.toFixed(1)})`, code: 'movement' };
  return { ok: true };
}

/**
 * Leave the seat; the unit stays where the emplacement stands — 63.1d: or steps out at the seat's exit (a vehicle's hatch
 * side, its bay's place behind the ramp), given a person's size to place it by.
 */
export function dismountUnit(state: GameState, playerId: number, cost = DISMOUNT_MOVEMENT_COST, person?: number): GameState {
  const player = state.players.find(p => p.id === playerId);
  if (!player?.mount) return state;
  const { bodyId: sid, seat: index } = player.mount;
  const body = state.structures.find(x => x.id === sid);
  const planet = body ? state.planets[body.planetIndex] : undefined;
  const out = body && planet ? seatPoint(body, index, 'exit', planet.radius, person) : null;
  return {
    ...state,
    players: state.players.map(p => {
      if (p.id !== playerId) return p;
      const { seatPos: _s, ...rest } = p;
      const left = { ...(p.seatPos === undefined ? p : rest), mount: undefined, movementRemaining: p.movementRemaining - cost, ...(out ? { surfaceDir: out } : {}) };
      if (!body || !isSatSeat(body.type, index)) return left;
      const { stance: _st, ...up } = left;   // 65.22: up from a sat seat, standing
      return up;
    }),
    structures: state.structures.map(x => { const seat = x.id === sid ? seatAt(x, index) : undefined; return !seat || seatUnit(seat) !== playerId ? x : withSeat(x, index, { ...seat, occupant: null }); }),
  };
}

/**
 * Keep seats and mounts consistent after anything that kills units or breaks structures: a
 * dead, missing or airborne occupant frees the seat; a destroyed or vanished emplacement unseats its unit.
 * Returns the same object when nothing changed.
 */
export function reconcileSeats(state: GameState): GameState {
  let players = state.players, structures = state.structures, changed = false;
  for (const s of state.structures) {
    s.seats?.forEach((seat, index) => {
      const uid = seatUnit(seat);
      if (uid === null) return;   // empty, or a crew record (19): nothing to reconcile
      const occ = state.players.find(p => p.id === uid);
      if (!occ || !occ.alive || occ.airborne || occ.mount?.bodyId !== s.id || occ.mount.seat !== index || s.health <= 0) {
        // decision 124: the hull died under a seated unit — below the hatch line it dies with it; at a hatch it bails out
        // with what reached it. A fixed gun's shield takes the blow whole (share 0: the occupant climbs out unhurt, as
        // before); a moving vehicle's death (step 5) passes the share that reached the hatch.
        // (63.12, vehicleZones: a vehicle shot to death burns out — everyone aboard bails out, the bays too)
        if (occ?.alive && s.health <= 0 && BODIES[s.type].kind === 'vehicle' && !s.gunfireKill && !hatchSurvival(BODIES[s.type].seats[index], 0, occ.health).survives) {
          players = players.map(q => q.id !== occ.id ? q : killUnit(q, 'wreck'));
        }
        structures = structures.map(x => x.id !== s.id ? x : withSeat(x, index, { ...seat, occupant: null }));
        changed = true;
      }
    });
  }
  for (const p of state.players) {
    if (!p.mount) continue;
    const s = state.structures.find(x => x.id === p.mount!.bodyId);
    // an airborne unit is not in its seat, whatever launched it (a geyser, a hazard; knockback skips the seated)
    if (!s || s.health <= 0 || !p.alive || p.airborne || seatUnit(seatAt(s, p.mount.seat)) !== p.id) {
      const ground = !!s && isSatSeat(s.type, p.mount.seat);
      players = players.map(q => {
        if (q.id !== p.id) return q;
        const { seatPos: _s, ...rest } = q;
        const off = { ...(q.seatPos === undefined ? q : rest), mount: undefined };   // 63.1d step 2: off its hatch's stand too
        if (!ground) return off;
        const { stance: _st, ...up } = off;   // 65.22: and up from a sat seat
        return up;
      });
      changed = true;
    }
  }
  return changed ? { ...state, players, structures } : state;
}

/** The crew a unit carries: the records in its own seats, or the implicit crew of one gunner every unit has today (ruling 19). */
export function crewOf(player: Pick<PlayerState, 'seats' | 'name' | 'health'>): CrewRecord[] {
  const crew = (player.seats ?? []).flatMap(s => s.occupant && 'crew' in s.occupant ? [s.occupant.crew] : []);
  return crew.length ? crew : [{ id: 'gunner', name: player.name, role: 'gunner', health: player.health }];
}

// -----------------------------------------------------------------------------
// 64.11: seats on the action clock
// -----------------------------------------------------------------------------

/** The nearest emplacement whose seat this person may take now, within `reach` (ties to the lower id), or null. */
export function seatInReach(state: Pick<GameState, 'structures' | 'planets' | 'players'>, player: PlayerState, reach: number, person?: number): number | null {
  return seatChoiceInReach(state, player, reach, person)?.structureId ?? null;
}

/**
 * 63.1d: the seat this person takes on E — the nearest free seat in reach of it: a one-seat body measured at where it
 * stands (today's rule), a vehicle's seats each at its own entry (`person` places them; without it, the one-seat rule).
 * Ties to the lower structure id, then the lower seat.
 */
export function seatChoiceInReach(state: Pick<GameState, 'structures' | 'planets' | 'players'>, player: PlayerState, reach: number, person?: number): { structureId: number; seat: number } | null {
  const planet = state.planets[player.planetIndex];
  if (!planet) return null;
  let best: { structureId: number; seat: number } | null = null, bestD = Infinity;
  for (const s of state.structures) {
    const pts = person !== undefined ? SEAT_POINTS[s.type as BodyKey] : undefined;
    if (!pts) {
      if (!seatAt(s, 0) || canMount(player, s.id, state, { reach, cost: 0 }).ok !== true) continue;
      const d = arcDistance(player.surfaceDir, s.surfaceDir, planet.radius);
      if (d < bestD || (d === bestD && best !== null && s.id < best.structureId)) { bestD = d; best = { structureId: s.id, seat: 0 }; }
      continue;
    }
    for (let i = 0; i < (s.seats?.length ?? 0); i++) {
      if (canMount(player, s.id, state, { reach, cost: 0, seat: i, person }).ok !== true) continue;
      const d = arcDistance(player.surfaceDir, seatPoint(s, i, 'entry', planet.radius, person)!, planet.radius);
      if (d < bestD || (d === bestD && best !== null && s.id < best.structureId)) { bestD = d; best = { structureId: s.id, seat: i }; }
    }
  }
  return best;
}

/** 63.1d's follow-up: a vehicle is in reach of a person when any of its seats' entries is (its seats are boarded round its hull). */
export function vehicleInReach(s: Pick<StructureState, 'type' | 'surfaceDir' | 'drive'>, player: Pick<PlayerState, 'surfaceDir'>, reach: number, planetRadius: number, person: number): boolean {
  const pts = SEAT_POINTS[s.type as BodyKey];
  if (!pts) return false;
  for (let i = 0; i < pts.length; i++) {
    const e = seatPoint(s, i, 'entry', planetRadius, person);
    if (e && arcDistance(player.surfaceDir, e, planetRadius) <= reach) return true;
  }
  return false;
}

/**
 * 63.1d's follow-up (the owner: "I can't ever get into the driver seat"): the seat a person asked for (`UnitIntent.seatPick`,
 * the host's pick under its crosshair) when it can be taken now — free, and for a vehicle with several seats, the vehicle
 * in reach (any of its entries); a one-seat body by the nearest rule's own test. Null: fall back to the nearest.
 */
export function pickedSeat(state: Pick<GameState, 'structures' | 'planets' | 'players'>, player: PlayerState, pick: { structureId: number; seat: number }, reach: number, person: number): { structureId: number; seat: number } | null {
  const s = state.structures.find(x => x.id === pick.structureId);
  const planet = state.planets[player.planetIndex];
  if (!s || !planet || !seatAt(s, pick.seat)) return null;
  if (SEAT_POINTS[s.type as BodyKey]) {
    if (!vehicleInReach(s, player, reach, planet.radius, person)) return null;
    return canMount(player, s.id, state, { reach: Infinity, cost: 0, seat: pick.seat, person }).ok ? { structureId: s.id, seat: pick.seat } : null;
  }
  return canMount(player, s.id, state, { reach, cost: 0, seat: pick.seat, person }).ok ? { structureId: s.id, seat: pick.seat } : null;
}

/**
 * 63.1d's follow-up: from a vehicle's bay, the front seat a second E moves up to — the picked one when it is a free seat
 * of this vehicle that is not a bay, else the driver's, else a gunner's (in seat order); null when none is free (E then
 * gets out). 73.7a: never the driver's of a mobility-killed hull (StructureState.disabled: nothing to drive).
 */
export function moveUpSeat(state: Pick<GameState, 'structures'>, player: PlayerState, pick?: { structureId: number; seat: number }): number | null {
  if (!player.mount) return null;
  const s = state.structures.find(x => x.id === player.mount!.bodyId);
  const defs = s ? BODIES[s.type].seats : undefined;
  if (!s || !defs) return null;
  // 69.35 (DECISIONS 252 (3): the commander's seat is the spare man's): from a tank's commander's seat (its gun seat without
  // the main gun) to a free gunner's seat, else a free driver's — the main gun and the wheel before the cupola (a bot's ask:
  // the host sends 'up' only from a bay)
  const commander = (i: number) => s.type === 'tank' && defs[i]?.role === 'weapon' && !s.seats?.[i]?.main;
  if (commander(player.mount.seat)) {
    const free = (i: number) => i >= 0 && !!seatAt(s, i) && !seatTaken(seatAt(s, i)!);
    const gunner = (s.seats ?? []).findIndex(x => !!x.main), driver = s.disabled ? -1 : defs.findIndex(d => d.role === 'driver');
    if (pick && pick.structureId === s.id && (pick.seat === gunner || pick.seat === driver) && free(pick.seat)) return pick.seat;
    return free(gunner) ? gunner : free(driver) ? driver : null;
  }
  if (defs[player.mount.seat]?.entry !== 'bay') return null;
  const front = (i: number) => !!defs[i] && defs[i].entry !== 'bay' && !(s.disabled && defs[i].role === 'driver') && !!seatAt(s, i) && !seatTaken(seatAt(s, i)!);
  if (pick && pick.structureId === s.id && front(pick.seat)) return pick.seat;
  for (const role of ['driver', 'weapon'] as const) for (let i = 0; i < defs.length; i++) if (defs[i].role === role && front(i)) return i;
  return null;
}

/**
 * DECISIONS 250 (the owner: "I only care about a key to switch from driver to gunner, doens't help to be able to switch to
 * commander"): the seat a switch moves a unit to — from a vehicle's driver's seat its first weapon seat (the main weapon's: a
 * tank's gunner, a technical's or a 6×6's gun), from that seat the driver's; never the commander's (a tank's second weapon
 * seat), nor from it or a passenger's. Null when the unit sits in neither or the vehicle has not both.
 */
export function crewSwitchSeat(state: Pick<GameState, 'structures'>, player: Pick<PlayerState, 'mount'>): number | null {
  if (!player.mount) return null;
  const s = state.structures.find(x => x.id === player.mount!.bodyId);
  const defs = s ? BODIES[s.type].seats : undefined;
  if (!s || !defs || !s.seats) return null;
  const driver = defs.findIndex(d => d.role === 'driver'), gunner = defs.findIndex(d => d.role === 'weapon');
  if (driver < 0 || gunner < 0 || !seatAt(s, driver) || !seatAt(s, gunner)) return null;
  return player.mount.seat === driver ? gunner : player.mount.seat === gunner ? driver : null;
}

export type SeatEvent = { kind: 'mounted'; unitId: number; structureId: number } | { kind: 'dismounted'; unitId: number; structureId: number }
  // 63.1k: a person took a seat its side's bot held (the bot out at the seat's exit; the Match shuns the gun for it)
  | { kind: 'seatTaken'; unitId: number; fromUnitId: number; structureId: number; seat: number }
  // 63.1q (the owner: "The prompt is still there to enter a destroyed vehicle, it just doesn't actually work"): a pick that
  // named a wreck — refused and said, not a silent nothing nor another seat instead
  | { kind: 'seatRefused'; unitId: number; structureId: number; reason: 'wrecked' }
  // DECISIONS 250: driver ↔ gunner inside the vehicle (`with`: the side's bot that sat there, now in the seat left)
  | { kind: 'seatSwitched'; unitId: number; structureId: number; from: number; to: number; with?: number };

/** 63.1q: can a body's seats be offered at all — standing (a wreck's hull is no ride: the page's E prompt skips it). */
export function hullOffersSeats(s: Pick<StructureState, 'health'>): boolean { return s.health > 0; }

/**
 * 64.11: the action clock's seat changes for one tick, from each unit's intent: everyone asking out gets out, then
 * everyone asking in gets the nearest free seat in reach — in unit order, so the first to ask takes a contested
 * seat. No movement cost (the action clock has none); taking an enemy's seat captures it, as in the turn game.
 */
export function stepSeats(state: GameState, seatIntents: ReadonlyArray<'enter' | 'leave' | 'up' | 'switch' | undefined>, reach: number, picks: ReadonlyArray<{ structureId: number; seat: number } | undefined> = [], held: (unitId: number) => boolean = () => false): { state: GameState; events: SeatEvent[] } {
  const events: SeatEvent[] = [];
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;   // 63.1d: a vehicle's seats are placed by the person's size
  let s = state;
  for (let i = 0; i < state.players.length; i++) {
    const p = s.players[i];
    if (seatIntents[i] !== 'leave' || !p.mount) continue;
    const sid = p.mount.bodyId;
    s = dismountUnit(s, p.id, 0, person);
    events.push({ kind: 'dismounted', unitId: p.id, structureId: sid });
  }
  // DECISIONS 250: a switch, driver ↔ gunner (crewSwitchSeat) — into the seat when it is free; when its own side's bot holds
  // it (living, held by no person) the two change places, so the vehicle keeps its crew; anyone else's, or a crew record's,
  // refuses it (nothing changes)
  for (let i = 0; i < state.players.length; i++) {
    const p = s.players[i];
    if (seatIntents[i] !== 'switch' || !p.mount || !p.alive || p.airborne) continue;
    const to = crewSwitchSeat(s, p), sid = p.mount.bodyId, from = p.mount.seat;
    const st = s.structures.find(x => x.id === sid);
    if (to === null || !st || !hullOffersSeats(st)) continue;
    const there = seatAt(st, to)!, occ = seatUnit(there);
    if (!seatTaken(there)) {
      s = loadShells(mountUnit(dismountUnit(s, p.id, 0, person), p.id, sid, 0, to), p.id);
      events.push({ kind: 'seatSwitched', unitId: p.id, structureId: sid, from, to });
      continue;
    }
    const bot = occ !== null ? s.players.find(q => q.id === occ) : undefined;
    if (!bot || !bot.alive || bot.teamIndex !== p.teamIndex || held(bot.id)) continue;
    s = dismountUnit(dismountUnit(s, bot.id, 0, person), p.id, 0, person);
    s = loadShells(mountUnit(s, bot.id, sid, 0, from), bot.id);
    s = loadShells(mountUnit(s, p.id, sid, 0, to), p.id);   // (the person last: the vehicle's owner, as a person's mount leaves it)
    events.push({ kind: 'seatSwitched', unitId: p.id, structureId: sid, from, to, with: bot.id });
  }
  for (let i = 0; i < state.players.length; i++) {
    const p = s.players[i];
    if ((seatIntents[i] !== 'enter' && seatIntents[i] !== 'up') || !p.alive || p.airborne) continue;
    if (seatIntents[i] === 'up' || p.mount) {
      // 63.1d's follow-up: 'up' in a vehicle's bay moves up to a free front seat (the picked one, else the driver's, else a
      // gunner's); 'enter' from a seat changes nothing, as before
      if (seatIntents[i] !== 'up' || !p.mount) continue;
      const to = moveUpSeat(s, p, picks[i]);
      if (to === null) continue;
      const sid = p.mount.bodyId;
      s = mountUnit(dismountUnit(s, p.id, 0), p.id, sid, 0, to);
      events.push({ kind: 'mounted', unitId: p.id, structureId: sid });
      continue;
    }
    const pick = picks[i];
    // 63.1k (the owner: "player needs the ability to take a seat / take over an emplacement"): a person (a unit a seat
    // holds, `held`) picking a seat its own side's bot holds takes it — the bot out at the seat's exit, the person in, the
    // same tick — when it could have taken that seat free (the same reach and rules); an enemy's seat, or a held unit's,
    // stays theirs
    if (pick && held(p.id)) {
      const st = s.structures.find(x => x.id === pick.structureId), occ = st ? seatUnit(seatAt(st, pick.seat)) : null;
      const bot = occ !== null && occ !== p.id ? s.players.find(q => q.id === occ) : undefined;
      if (bot && bot.alive && bot.teamIndex === p.teamIndex && !held(bot.id)) {
        const freed = dismountUnit(s, bot.id, 0, person);
        const me = freed.players.find(q => q.id === p.id)!;
        if (pickedSeat(freed, me, pick, reach, person)) {
          s = loadShells(mountUnit(freed, p.id, pick.structureId, 0, pick.seat), p.id);
          events.push({ kind: 'dismounted', unitId: bot.id, structureId: pick.structureId }, { kind: 'mounted', unitId: p.id, structureId: pick.structureId },
            { kind: 'seatTaken', unitId: p.id, fromUnitId: bot.id, structureId: pick.structureId, seat: pick.seat });
          continue;
        }
      }
    }
    // 63.1q: a person's pick (a unit a seat holds) that names a wreck is refused, and said (a held key or a race with the
    // kill: the prompt is gone already); a bot's is lane B's half (its boarding loop), left as it was
    const pickedHull = pick && held(p.id) ? s.structures.find(x => x.id === pick.structureId) : undefined;
    if (pickedHull && !hullOffersSeats(pickedHull)) { events.push({ kind: 'seatRefused', unitId: p.id, structureId: pickedHull.id, reason: 'wrecked' }); continue; }
    const choice = (pick ? pickedSeat(s, p, pick, reach, person) : null) ?? seatChoiceInReach(s, p, reach, person);
    if (choice === null) continue;
    s = loadShells(mountUnit(s, p.id, choice.structureId, 0, choice.seat), p.id);   // decision 128: shells it carried go into the gun
    events.push({ kind: 'mounted', unitId: p.id, structureId: choice.structureId });
  }
  return { state: s, events };
}

/**
 * Danger close: would a shell of `weaponKey` landing at `point` reach the gun it was fired from? Its own blast's reach
 * of a structure: under the clock policy's `blastReach: 'shape'` the damage circle from the gun's shape (63.4 step 7,
 * core/blastReach.ts), else core/damage.ts structureBlastReach from its centre. A crew holds such a shot.
 */
export function gunDangerClose(state: Pick<GameState, 'planets' | 'config' | 'rocks' | 'flora' | 'structures'>, gun: StructureState, point: Vec3, weaponKey: WeaponKey): boolean {
  const planet = state.planets[gun.planetIndex];
  if (!planet) return false;
  const radius = WEAPONS[weaponKey].blastRadius;
  const at = getStructureWorldPosition(gun, state.planets as GameState['planets']);
  if (clockPolicyFor(state.config).blastReach === 'shape') return (structureNearest(point, gun, state)?.dist ?? vecLength(vecSub(point, at))) < radius * 2.2;
  return vecLength(vecSub(point, at)) < structureBlastReach(radius, planet.radius, personScaleStructureHalfWidth(gun, state.config));
}

// ---- Decision 128 (the owner, Oct 10): a gun's shells, at a person scale on the action clock ---------------------

/**
 * The decision-128 gun rules apply: a person scale (at classic scale people and guns share the planet's family). The
 * scale is the whole test — it never reads the clock: every caller is already on the action clock's path (the gun's
 * fire in the Match's action step, the crates' restock, loading on getting in, the action bot), or is setup.
 */
export function gunRulesApply(state: Pick<GameState, 'config'>): boolean {
  return personSetOf(state).k < 1;
}
/** "Shell stock + resupply": a gun holds GUN_STOCK's shells, refilled by a load carried from a crate. */
export function gunStockOn(state: Pick<GameState, 'config'>): boolean {
  return gunRulesApply(state) && state.config.gunStock !== false;
}
/** "Accuracy penalty without spotter, and slower reload without spotter" (core/actionShells.ts aimSpotted). */
export function gunSpottingOn(state: Pick<GameState, 'config'>): boolean {
  return gunRulesApply(state) && state.config.gunSpotting !== false;
}

/** Shells left in a seat's gun for a weapon (Infinity when the gun keeps no stock). */
export function shellsLeft(state: Pick<GameState, 'config'>, seat: Pick<SeatState, 'shells'>, weaponKey: WeaponKey): number {
  if (!gunStockOn(state)) return Infinity;
  return seat.shells?.[weaponKey] ?? (GUN_STOCK.shells[weaponKey] ?? 0);
}

/** Shells a seat's gun is short of its stock, over its weapons (0 without a stock). */
export function shellsShort(state: Pick<GameState, 'config'>, seat: Pick<SeatState, 'shells' | 'weapons'>): number {
  if (!gunStockOn(state)) return 0;
  return seat.weapons.reduce((n, w) => n + Math.max(0, (GUN_STOCK.shells[w] ?? 0) - shellsLeft(state, seat, w)), 0);
}

/** No shell left in any of the seat's weapons. */
export function gunOutOfShells(state: Pick<GameState, 'config'>, seat: Pick<SeatState, 'shells' | 'weapons'> & Partial<Pick<SeatState, 'guns' | 'launcher'>>): boolean {
  if (seat.guns?.length || seat.launcher) return false;   // 65.22: a mounted bullet gun's belt reloads itself — never dry for shells; 69.21: nor a launcher (its store is its own)
  return seat.weapons.every(w => shellsLeft(state, seat, w) <= 0);
}

/** The seat with `count` shells of a weapon (a full weapon drops out of the record: absent = full). */
function withShells(seat: SeatState, weaponKey: WeaponKey, count: number): SeatState {
  const shells: Partial<Record<WeaponKey, number>> = { ...(seat.shells ?? {}) };
  if (count >= (GUN_STOCK.shells[weaponKey] ?? 0)) delete shells[weaponKey];
  else shells[weaponKey] = count;
  const { shells: _old, ...rest } = seat;
  return Object.keys(shells).length > 0 ? { ...rest, shells } : rest;
}

/** One shell of a weapon spent (no stock: unchanged). */
export function spendShell(state: Pick<GameState, 'config'>, seat: SeatState, weaponKey: WeaponKey): SeatState {
  if (!gunStockOn(state)) return seat;
  return withShells(seat, weaponKey, shellsLeft(state, seat, weaponKey) - 1);
}

/**
 * The unit in a gun's seat loads the shells it carries: each weapon up to its stock, in the seat's weapon order; what
 * does not fit stays carried. Unchanged without a stock, a load or a seat.
 */
export function loadShells(state: GameState, unitId: number): GameState {
  const p = state.players.find(q => q.id === unitId);
  if (!gunStockOn(state) || !p?.mount || !p.shellLoad) return state;
  const st = state.structures.find(s => s.id === p.mount!.bodyId);
  const seat = st ? seatAt(st, p.mount.seat) : undefined;
  if (!st || !seat) return state;
  let load = p.shellLoad, next = seat;
  for (const w of seat.weapons) {
    const short = Math.max(0, (GUN_STOCK.shells[w] ?? 0) - shellsLeft(state, next, w));
    const put = Math.min(short, load);
    if (put > 0) { next = withShells(next, w, shellsLeft(state, next, w) + put); load -= put; }
  }
  if (next === seat) return state;
  return {
    ...state,
    players: state.players.map(q => q.id !== unitId ? q : load > 0 ? { ...q, shellLoad: load } : (({ shellLoad: _l, ...rest }) => rest)(q)),
    structures: state.structures.map(s => s.id !== st.id ? s : withSeat(s, p.mount!.seat, next)),
  };
}
